import * as React from "react";
import { SubAccountType } from "../../Wrap/Constants/Enums/Domain/SubAccountType";

export class StringHelper {
    private static defaultRegex = /{[0-9]?}/;

    private setting;

    public constructor() {
        this.setting = {};
    }

    public static interpolate(
        textToBeFormatted: string,
        inputs: any[],
        regex: RegExp = StringHelper.defaultRegex
    ): string {
        if (textToBeFormatted == null || inputs == null || inputs.filter((v) => v == null).length > 0) {
            return "";
        }

        const _r = (accumulator: string, current: string): string => {
            return accumulator.replace(regex, current);
        };

        return inputs.reduce(_r, textToBeFormatted);
    }

    public static stripSpecialCharacter(value) {
        const pattern = new RegExp("[^0-9a-zA-Z]");
        let rs = "";
        for (let i = 0; i < value.length; i++) {
            rs = rs + value.substr(i, 1).replace(pattern, "");
        }
        return rs;
    }

    public getDonutClass(type: string | SubAccountType): string {
        let _class = " ";
        const dic: any[] = [
            { key: SubAccountType.StocksIsa, name: "stocksisa", value: "isa", displayName: "ISA Stocks & Shares" }, ////ISA Stocks & Shares
            { key: SubAccountType.Pension, name: "pension", value: "sipp", displayName: "SIPP" },
            { key: SubAccountType.Sipp, name: "sipp", value: "sipp", displayName: "SIPP" },
            {
                key: SubAccountType.InvestmentAccount,
                name: "investmentaccount",
                value: "pp",
                displayName: "Personal Portfolio",
            }, //Personal Portfolio
            { key: SubAccountType.CashAccount, name: "cashaccount", value: "wrap-cash", displayName: "Cash Account" },
            { key: SubAccountType.WrapCash, name: "wrapcash", value: "wrap-cash", displayName: "Cash Account" },
            {
                key: SubAccountType.OffshoreBond,
                name: "offshorebond",
                value: "offshore-bond",
                displayName: "Offshore Bond",
            },
            {
                key: SubAccountType.OnshoreBond,
                name: "onshorebond",
                value: "onshore-bond",
                displayName: "Onshore Bond",
            },
            { key: SubAccountType.CashIsa, name: "cashisa", value: "cash-isa", displayName: "ISA Cash" },
            { key: SubAccountType.NotSpecified, name: "total", value: "total", displayName: "Total" },
        ];
        _class +=
            typeof type == "string"
                ? dic.filter((x) => x.name == (type + "").toLowerCase() || x.displayName == type)[0]?.value
                : dic.filter((x) => x.key == type)[0]?.value;
        const preCount: number = this.setting?.[_class] ? this.setting?.[_class] : 0;
        const curCount = preCount + 1;
        const suffer = curCount == 1 ? "" : `-${curCount.toString()}`;
        this.setting[_class] = curCount;

        return `${_class}${suffer}`;
    }

    public static getSubAccountTypeDisplayName(subAccountType: number): string {
        const subAccountTypeMapper = {
            [SubAccountType.NotSpecified]: "Not Specified",
            [SubAccountType.InvestmentAccount]: "Investment Account",
            [SubAccountType.CashIsa]: "Cash Isa",
            [SubAccountType.StocksIsa]: "Strocks Isa",
            [SubAccountType.AuAccumulation]: "Au Accumulation",
            [SubAccountType.AuPension]: "Au Pension",
            [SubAccountType.Pension]: "Pension",
            [SubAccountType.OffshoreBond]: "Offshore Bond",
            [SubAccountType.OnshoreBond]: "Onshore Bond",
            [SubAccountType.CashAccount]: "Cash Account",
            [SubAccountType.Sipp]: "Sipp",
            [SubAccountType.RetailSipp]: "Retail Sipp",
            [SubAccountType.PersonalPortfolio]: "Personal Portfolio",
            [SubAccountType.RetailBond]: "Retail Bond",
            [SubAccountType.WrapCash]: "Wrap Cash",
            [SubAccountType.Pep]: "Pep",
            [SubAccountType.OrsoRa]: "Orso Ra",
            [SubAccountType.OrsoSa]: "Orso Sa",
            [SubAccountType.JuniorIsa]: "Junior Isa",
            [SubAccountType.JuniorSipp]: "Junior Sipp",
            [SubAccountType.Npr]: "NPR",
            [SubAccountType.Pr]: "Pr",
            [SubAccountType.Other]: "Other",
        };

        return subAccountTypeMapper[subAccountType];
    }

    public static ConvertBooleanToString(value: boolean, trueString = "Yes", falseString = "No") {
        if (trueString == null) {
            trueString = "Yes";
        }
        if (falseString == null) {
            falseString = "No";
        }
        return value ? trueString : falseString;
    }

    public static getHighlightedText(text, highlight) {
        let chunks = highlight
            .filter((searchWord) => searchWord) // Remove empty words
            .reduce((chunks, searchWord) => {
                const regex = new RegExp(searchWord, "gi");

                let match;
                while ((match = regex.exec(text))) {
                    const start = match.index;
                    const end = regex.lastIndex;
                    // We do not return zero-length matches
                    if (end > start) {
                        chunks.push({ start, end });
                    }
                    // Prevent browsers like Firefox from getting stuck in an infinite loop
                    if (match.index === regex.lastIndex) {
                        regex.lastIndex++;
                    }
                }

                return chunks;
            }, []);

        chunks = chunks
            .sort((first, second) => first.start - second.start)
            .reduce((processedChunks, nextChunk) => {
                // First chunk just goes straight in the array...
                if (processedChunks.length === 0) {
                    return [nextChunk];
                } else {
                    // ... subsequent chunks get checked to see if they overlap...
                    const prevChunk = processedChunks.pop();
                    if (nextChunk.start <= prevChunk.end) {
                        // It may be the case that prevChunk completely surrounds nextChunk, so take the
                        // largest of the end indeces.
                        const endIndex = Math.max(prevChunk.end, nextChunk.end);
                        processedChunks.push({ highlight: false, start: prevChunk.start, end: endIndex });
                    } else {
                        processedChunks.push(prevChunk, nextChunk);
                    }
                    return processedChunks;
                }
            }, []);

        const allChunks = [];
        const append = (start, end, highlight) => {
            if (end - start > 0) {
                allChunks.push({
                    start,
                    end,
                    highlight,
                });
            }
        };

        if (chunks.length === 0) {
            append(0, text.length, false);
        } else {
            let lastIndex = 0;
            chunks.forEach((chunk) => {
                append(lastIndex, chunk.start, false);
                append(chunk.start, chunk.end, true);
                lastIndex = chunk.end;
            });
            append(lastIndex, text.length, false);
        }

        return allChunks.map((ele) => {
            if (ele.highlight) {
                return (
                    <span>
                        <b>{text.substr(ele.start, ele.end - ele.start)}</b>
                    </span>
                );
            } else {
                return <span>{text.substr(ele.start, ele.end - ele.start)}</span>;
            }
        });
    }
}
